
import type React from "react"
import { useState, useEffect } from "react"
import { AppleMultiStepModal } from "@components/react/pricing/apple-multi-step-modal"

interface CustomQuoteModalProps {
  isOpen: boolean
  onClose: () => void
  selectedPlan?: string
  volumePricingMessage?: string
  currentSite: Page.Site
  invalidDomains: Array<string>
}

export function CustomQuoteModal({ isOpen, onClose, selectedPlan, volumePricingMessage, currentSite, invalidDomains=[] }: CustomQuoteModalProps) {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    jobTitle: "",
    country: "",
    state: "",
    optIn: false,
    message: selectedPlan ? `I'm interested in the ${selectedPlan} plan.` : "",
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [showStateField, setShowStateField] = useState(false)
  const [focusedField, setFocusedField] = useState<string | null>(null)

  // Update useEffect to set the message when selectedPlan or volumePricingMessage changes
  useEffect(() => {
    if (volumePricingMessage) {
      setFormData((prev) => ({
        ...prev,
        message: volumePricingMessage,
      }))
    } else if (selectedPlan) {
      setFormData((prev) => ({
        ...prev,
        message: `I'm interested in the ${selectedPlan} plan.`,
      }))
    }
  }, [selectedPlan, volumePricingMessage])

  // Show state field only for US, Canada, and Australia
  useEffect(() => {
    setShowStateField(["United States", "Canada", "Australia"].includes(formData.country))
  }, [formData.country])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type, checked } = e.target as HTMLInputElement
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  const handleSubmit = () => {
    setIsSubmitting(true)

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false)
      setIsSubmitted(true)

      // Reset form after 3 seconds
      setTimeout(() => {
        setIsSubmitted(false)
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          company: "",
          jobTitle: "",
          country: "",
          state: "",
          optIn: false,
          message: "",
        })
        onClose()
      }, 3000)
    }, 1500)
  }

  // Get modal title based on context
  const getModalTitle = () => {
    if (volumePricingMessage) {
      return `Volume Pricing for ${selectedPlan}`
    } else if (selectedPlan) {
      return `Request Quote for ${selectedPlan}`
    } else {
      return "Request a Custom Quote"
    }
  }

  // Define steps for the multi-step form
  const steps = [
    {
      title: "Your Information",
      description: "Tell us about yourself",
    },
    {
      title: "Company Details",
      description: "Tell us about your organization",
    },
    {
      title: "Confirm Request",
      description: "Review and submit your request",
    },
  ]

  // Animation variants for form elements
  const formItemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.05,
        duration: 0.3,
        ease: "easeOut",
      },
    }),
  }

  return (
    <AppleMultiStepModal
      currentSite={currentSite}
      invalidDomains={invalidDomains}
      isOpen={isOpen}
      onClose={onClose}
      title={getModalTitle()}
      steps={steps}
      showSuccessState={isSubmitted}
      successTitle="Thank you!"
      successMessage="Your request has been submitted. Our team will contact you shortly."
      onComplete={handleSubmit}
      selectedPlan={selectedPlan}
      volumePricingMessage={volumePricingMessage}
    >

    </AppleMultiStepModal>
  )
}
